
import { Shell } from "@/components/layout/Shell";
import { useEffect, useState, useMemo } from "react";
import { DashboardWidgets } from "@/components/dashboard/DashboardWidgets";
import { DashboardControls } from "@/components/dashboard/DashboardControls";
import { WidgetsModal } from "@/components/dashboard/WidgetsModal";
import { DashboardYearFilter } from "@/components/dashboard/DashboardYearFilter";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useWidgetInitializer } from "@/hooks/widgets/useWidgetInitializer";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { FeatureGuard } from "@/components/common/FeatureGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PlanSelectDialogProvider, usePlanSelectDialog } from "@/contexts/PlanSelectDialogContext";
import { PlanSelectDialog } from "@/components/settings/PlanSelectDialog";
import { useEnhancedTranslation } from "@/hooks/useEnhancedTranslation";

export default function DashboardPage() {
  const { t } = useEnhancedTranslation();
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const { isLoading: isFeatureAccessLoading } = useFeatureAccess();
  const navigate = useNavigate();

  useWidgetInitializer();

  const {
    handlesVisible,
    isSaving,
    savedToDb,
    layoutInitialized,
    isWidgetsModalOpen,
    allWidgetItems,
    layoutType,
    setIsWidgetsModalOpen,
    toggleHandlesVisibility,
    resetDashboardLayout,
    handleSaveLayout,
    handleRemoveWidget,
    handleAddWidget,
    handleWidgetsReorder,
    updateWidgetSize,
    updateWidgetPosition,
    changeLayoutType
  } = useDashboardLayout('user-dashboard');

  const {
    userStats,
    contractsTimeline,
    contractsDistribution,
    recentContracts,
    upcomingExpirations,
    isLoading,
    fetchDashboardData
  } = useDashboardData();

  const hasData = useMemo(() => {
    return (
      userStats.totalContracts > 0 ||
      contractsTimeline.some(item => item.count > 0) ||
      contractsDistribution.length > 0 ||
      recentContracts.length > 0 ||
      upcomingExpirations.length > 0
    );
  }, [userStats, contractsTimeline, contractsDistribution, recentContracts, upcomingExpirations]);

  useEffect(() => {
    fetchDashboardData(selectedYear);
  }, [fetchDashboardData, selectedYear]);

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
  };

  // Use the plan dialog context to open modal
  const dashboardFallback = (
    <PlanSelectDialogProvider>
      <DashboardFallbackContent />
      <PlanSelectDialog />
    </PlanSelectDialogProvider>
  );

  return (
    <PlanSelectDialogProvider>
      <Shell className="p-4 md:p-6 space-y-6">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold tracking-tight">{t('dashboard.title', 'Dashboard')}</h1>
            <DashboardYearFilter
              selectedYear={selectedYear}
              onYearChange={handleYearChange}
            />
          </div>

          <FeatureGuard feature="dashboard" fallback={dashboardFallback}>
            <DashboardControls
              handlesVisible={handlesVisible}
              toggleHandlesVisibility={toggleHandlesVisibility}
              saveLayoutToDatabase={handleSaveLayout}
              resetDashboardLayout={resetDashboardLayout}
              isSaving={isSaving}
              savedToDb={savedToDb}
              openWidgetsModal={() => setIsWidgetsModalOpen(true)}
            />
            <WidgetsModal
              isOpen={isWidgetsModalOpen}
              onClose={() => setIsWidgetsModalOpen(false)}
              activeWidgetIds={allWidgetItems.map(item => item.id)}
              onAddWidget={handleAddWidget}
              dashboardType="user"
            />
            {isLoading || isFeatureAccessLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
                  <h3 className="text-lg font-medium">{t('dashboard.loading', 'Loading dashboard...')}</h3>
                </div>
              </div>
            ) : layoutInitialized && (
              <div className="dashboard-container">
                <DashboardWidgets
                  widgetItems={allWidgetItems}
                  handlesVisible={handlesVisible}
                  onRemove={handleRemoveWidget}
                  onResize={updateWidgetSize}
                  onReorder={handleWidgetsReorder}
                  onUpdatePosition={updateWidgetPosition}
                  userStats={userStats}
                  contractsTimeline={contractsTimeline}
                  contractsDistribution={contractsDistribution}
                  recentContracts={recentContracts}
                  upcomingExpirations={upcomingExpirations}
                  hasData={hasData}
                  selectedYear={selectedYear}
                  layoutType={layoutType}
                  onLayoutTypeChange={changeLayoutType}
                />
              </div>
            )}
          </FeatureGuard>
        </div>
        <PlanSelectDialog />
      </Shell>
    </PlanSelectDialogProvider>
  );
}

// Extracted fallback content for clarity
function DashboardFallbackContent() {
  const { t } = useEnhancedTranslation();
  const { openDialog } = usePlanSelectDialog();
  return (
    <Card className="p-8 flex flex-col items-center justify-center text-center space-y-6 bg-yellow-50 dark:bg-yellow-900/20 max-w-4xl mx-auto">
      <AlertTriangle className="h-16 w-16 text-amber-500" />
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">{t('dashboard.notAvailable', 'Dashboard Not Available')}</h2>
        <p className="text-lg text-muted-foreground max-w-md mx-auto">
          {t('dashboard.upgradeMessage', 'The advanced dashboard visualization is not included in your current subscription plan.')}
        </p>
        <p className="text-muted-foreground">
          {t('dashboard.upgradeDetails', 'Upgrade your plan to get access to all dashboard features including financial insights, contract timelines, distribution analytics, and more.')}
        </p>
      </div>
      <Button
        size="lg"
        onClick={openDialog}
        className="mt-2"
      >
        {t('dashboard.viewPlans', 'View Available Plans')}
      </Button>
    </Card>
  );
}
